import { Component, OnInit, OnDestroy, AfterViewInit, Input, Output, EventEmitter, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { RouteDetailService } from '../../services/route-detail.service';
import { DateSettings } from '../../viewModels/date-settings-viewmodel';
import { HistogramData, HistogramValue } from '../../../domain/models/histogramData';
import { Observable, Subscription } from 'rxjs';
import { TranslateService } from 'ng2-translate';

import * as d3 from 'd3';
import * as _ from 'lodash';
import { forEach } from '@angular/router/src/utils/collection';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-histogram',
  templateUrl: './histogram.component.html',
  styleUrls: ['./histogram.component.scss']
})
export class HistogramComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('base') base: ElementRef;
  @ViewChild('histogram') histogram: ElementRef;

  @Input() dateChangedObs: Observable<DateSettings>;
  @Input() binsChangedObs: Observable<number>;
  @Input() downloadObs: Observable<any>;
  @Output() onComponentReady: EventEmitter<ElementRef> = new EventEmitter();

  public jobId: string;
  public date: Date;

  public binSize: number;
  private data: HistogramData;
  private subscribtions: Subscription[];
  private noData: boolean;

  constructor(private routeDetailService: RouteDetailService,
    private translate: TranslateService) { }

  ngOnInit() {
    this.binSize = 0;
    this.subscribtions = [];
    this.noData = false;

    if (this.downloadObs) {
      this.downloadObs.subscribe(() => {
        this.onComponentReady.emit(this.histogram);
      });
    }
  }

  ngAfterViewInit(): void {
    this.getHistogramData();
    this.subscribeToDateChange();
    this.subscribeToBinsChanged();
  }

  getHistogramData(): void {
    if (!this.jobId) {
      return;
    }

    this.routeDetailService.getHistogram(this.jobId, this.date, this.binSize)
      .subscribe((result: HistogramData) => {
        if (result) {
          this.noData = false;
          this.data = result;
          setTimeout(() => {
            this.createGraph();
          }, 0);
        } else {
          this.noData = true;
        }
      }, (error: any) => {
        this.noData = true;
      });
  }

  subscribeToDateChange(): void {
    this.subscribtions.push(this.dateChangedObs.subscribe((settings: DateSettings) => {
      this.jobId = settings.routeId;
      this.date = settings.date;
      this.getHistogramData();
    }));
  }

  subscribeToBinsChanged(): void {
    this.subscribtions.push(this.binsChangedObs.subscribe((bins: number) => {
      this.binSize = bins;
      this.getHistogramData();
    }));
  }

  createGraph(): void {
    d3.select('svg').remove();

    let formatCount = d3.format(',.0f');
    let formatTime = d3.timeFormat('%M:%S');
    let formatMinutes = function (d) { return formatTime(new Date(2012, 0, 1, 0, 0, d)); };

    let margin = { top: 12, right: 30, bottom: 60, left: 30 };
    let width = this.base.nativeElement.clientWidth - margin.left - margin.right;
    let height = this.base.nativeElement.clientHeight - margin.top - margin.bottom;

    let minBinX = this.data.values[0].bin;
    let maxBinX = this.data.values[this.data.values.length - 1].bin + this.data.values[0].width;
    let binWidth = this.data.values[0].width + this.data.values[0].bin;


    let xLabel = this.translate.instant('HISTOGRAM_X_LABEL');
    let yLabel = this.translate.instant('HISTOGRAM_Y_LABEL');

    let x = d3.scaleLinear()
      .domain([minBinX, maxBinX])
      .range([0, width]);

    let y = d3.scaleLinear()
      .domain([0, d3.max(this.data.values, function (d: HistogramValue) { return d.value; })])
      .range([height, 0]);

    let tickvalues = [];
    _.forEach(this.data.values, (value: HistogramValue) => {
      tickvalues.push(value.bin);
    });
    tickvalues.push(maxBinX);
    let xAxis = d3.axisBottom()
      .scale(x)
      .tickValues(tickvalues)
      .tickFormat(formatMinutes);

    // D3 Classes are defined in style.css (component css is not accepted by svg)
    let svg = d3.select(this.base.nativeElement).append('svg')
      .attr('xmlns', 'http://www.w3.org/2000/svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .style('fill', '#F5FAFF');

    let bar = svg.selectAll('.bar')
      .data(this.data.values)
      .enter().append('g')
      .attr('class', 'bar')
      .attr('transform', function (d: HistogramValue) { return 'translate(' + x(d.bin) + ',' + y(d.value) + ')'; });

    bar.append('rect')
      .attr('x', 1)
      .attr('width', x(binWidth) - 1)
      .attr('height', function (d: HistogramValue) { return height - y(d.value); })
      .style('fill', '#d1effc')
      .style('outline', '#fff')
      .style('outline-width', '1px')
      .style('outline-style', 'solid');


    bar.append('text')
      .attr('dy', '.75em')
      .attr('y', -12)
      .attr('x', x(this.data.values[0].bin + this.data.values[0].width / 2))
      .attr('text-anchor', 'middle')
      .style('font-family', 'Roboto Condensed, sans-sarif')
      .style('font-size', '14px')
      .text(function (d) { return Math.round(d.values.length * 100) / 100; });

    svg.append('text')
      .attr('transform',
        'translate(' + (width / 2) + ' ,' + (height + margin.top + 25) + ')')
      .style('text-anchor', 'middle')
      .style('font-family', 'Roboto Condensed, sans-sarif')
      .style('font-size', '13px')
      .text(xLabel);

    svg.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - margin.left)
      .attr('x', 0 - (height / 2))
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .style('font-family', 'Roboto Condensed, sans-sarif')
      .style('font-size', '13px')
      .text(yLabel);

    svg.append('g')
      .style('font-size', '12px')
      .attr('class', 'x axis')
      .attr('transform', 'translate(0,' + height + ')')
      .call(xAxis);
  }

  onResize() {
    this.createGraph();
  }

  noDataAvailable(): boolean {
    return this.noData;
  }

  ngOnDestroy() {
    _.each(this.subscribtions, (sub: Subscription) => {
      sub.unsubscribe();
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/route-detail/components/histogram/histogram.component.ts